package co.uk.sacursoftware.ssinventory.data.repository

import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.util.AppLogger
import kotlin.coroutines.cancellation.CancellationException

class SSInventoryRepository(private val api: SSInventoryApi) {

    private suspend fun <T> wrapWithLog(action: String, params: String = "", call: suspend () -> T): Result<T> {
        AppLogger.d("API Call: $action | Params: $params")
        return try {
            val result = call()
            AppLogger.d("API Success: $action")
            
            if (result is AppResponse && result.error != null) {
                AppLogger.e("API Backend Error: $action | ${result.error}")
                Result.success(result)
            } else {
                Result.success(result)
            }
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            AppLogger.e("API Failure: $action | Error: ${e.message}", e)
            Result.failure(Exception(e.message ?: "Request failed", e))
        }
    }

    suspend fun getSuppliers(): Result<List<Supplier>> = wrapWithLog("get_suppliers") {
        api.getSuppliers().filter { it.id != "99" && !it.name.equals("ARCHIVED", ignoreCase = true) }
    }

    suspend fun getItems(supplierId: String): Result<List<Item>> = wrapWithLog("get_items", "supplierId=$supplierId") {
        api.getItems(supplierId)
    }

    suspend fun getStockCounts(supplierId: String): Result<List<StockCount>> = wrapWithLog("get_counts", "supplierId=$supplierId") {
        api.getStockCounts(supplierId)
    }

    suspend fun getStockCountDetails(countId: String): Result<List<StockCountItem>> = wrapWithLog("get_count_details", "countId=$countId") {
        api.getStockCountDetails(countId)
    }

    suspend fun getOrders(supplierId: String): Result<List<Order>> = wrapWithLog("get_orders", "supplierId=$supplierId") {
        api.getOrders(supplierId)
    }

    suspend fun getOrderItems(orderId: String): Result<List<OrderItem>> = wrapWithLog("get_order_items", "orderId=$orderId") {
        api.getOrderItems(orderId)
    }

    suspend fun getReports(): Result<List<Report>> = wrapWithLog("get_reports") {
        api.getReports()
    }

    suspend fun getReportDetails(reportId: String): Result<List<ReportDetail>> = wrapWithLog("get_report_details", "reportId=$reportId") {
        api.getReportDetails(reportId)
    }

    suspend fun createSupplier(request: CreateSupplierRequest): Result<AppResponse> = wrapWithLog("create_supplier", request.toString()) {
        api.createSupplier(request)
    }

    suspend fun editSupplier(request: EditSupplierRequest): Result<AppResponse> = wrapWithLog("edit_supplier", request.toString()) {
        api.editSupplier(request)
    }

    suspend fun createItem(request: CreateItemRequest): Result<AppResponse> = wrapWithLog("create_item", request.toString()) {
        api.createItem(request)
    }

    suspend fun editItem(request: EditItemRequest): Result<AppResponse> = wrapWithLog("edit_item", request.toString()) {
        api.editItem(request)
    }

    suspend fun createStockCount(request: CreateStockCountRequest): Result<AppResponse> = wrapWithLog("create_stock_count", request.toString()) {
        api.createStockCount(request)
    }

    suspend fun editStockCount(request: EditStockCountRequest): Result<AppResponse> = wrapWithLog("edit_stock_count", request.toString()) {
        api.editStockCount(request)
    }

    suspend fun deleteStockCount(countId: String): Result<AppResponse> = wrapWithLog("delete_stock_count", "countId=$countId") {
        api.deleteStockCount(countId)
    }

    suspend fun createOrder(request: CreateOrderRequest): Result<AppResponse> = wrapWithLog("create_order", request.toString()) {
        api.createOrder(request)
    }

    suspend fun editOrder(request: EditOrderRequest): Result<AppResponse> = wrapWithLog("edit_order", request.toString()) {
        api.editOrder(request)
    }

    suspend fun deleteOrder(orderId: String): Result<AppResponse> = wrapWithLog("delete_order", "orderId=$orderId") {
        api.deleteOrder(orderId)
    }

    suspend fun deleteReport(reportId: String): Result<AppResponse> = wrapWithLog("delete_report", "reportId=$reportId") {
        api.deleteReport(reportId)
    }

    suspend fun generateReport(request: GenerateReportRequest): Result<AppResponse> = wrapWithLog("generate_report", request.toString()) {
        api.generateReport(request)
    }

    suspend fun getProfile(): Result<List<ProfileItem>> = wrapWithLog("get_profile") {
        api.getProfile()
    }

    suspend fun saveProfile(request: SaveProfileRequest): Result<AppResponse> = wrapWithLog("save_profile", request.toString()) {
        api.saveProfile(request)
    }

    suspend fun getImages(foreignId: String): Result<List<AppImage>> = wrapWithLog("get_images", "foreignId=$foreignId") {
        api.getImages(foreignId)
    }

    suspend fun getDaily(date: String): Result<DailyRecord?> = wrapWithLog("get_daily", "date=$date") {
        api.getDaily(date)
    }

    suspend fun saveDailyImages(request: SaveDailyImagesRequest): Result<AppResponse> = wrapWithLog("save_daily_images", request.toString()) {
        api.saveDailyImages(request)
    }

    suspend fun getDailyRoutines(date: String? = null): Result<List<DailyRoutine>> = wrapWithLog("get_daily_routines", "date=$date") {
        api.getDailyRoutines(date)
    }

    suspend fun saveDailyRoutine(request: SaveDailyRoutineRequest): Result<AppResponse> = wrapWithLog("save_daily_routine", request.toString()) {
        api.saveDailyRoutine(request)
    }

    suspend fun deleteDailyRoutine(id: String): Result<AppResponse> = wrapWithLog("delete_daily_routine", "id=$id") {
        api.deleteDailyRoutine(id)
    }

    suspend fun toggleDailyRoutine(id: String, date: String): Result<AppResponse> = wrapWithLog("toggle_daily_routine", "id=$id date=$date") {
        api.toggleDailyRoutine(id, date)
    }

    suspend fun getMasterPrepItems(): Result<List<PrepItem>> = wrapWithLog("get_master_prep_items") {
        api.getMasterPrepItems()
    }

    suspend fun saveMasterPrepItem(request: SavePrepItemRequest): Result<AppResponse> = wrapWithLog("save_master_prep_item", request.toString()) {
        api.saveMasterPrepItem(request)
    }

    suspend fun deleteMasterPrepItem(id: String): Result<AppResponse> = wrapWithLog("delete_master_prep_item", "id=$id") {
        api.deleteMasterPrepItem(id)
    }

    suspend fun getPrepRoutines(dayOfWeek: String? = null): Result<List<PrepRoutine>> = wrapWithLog("get_prep_routines", "dayOfWeek=$dayOfWeek") {
        api.getPrepRoutines(dayOfWeek)
    }

    suspend fun savePrepRoutine(request: SavePrepRoutineRequest): Result<AppResponse> = wrapWithLog("save_prep_routine", request.toString()) {
        api.savePrepRoutine(request)
    }

    suspend fun deletePrepRoutine(id: String): Result<AppResponse> = wrapWithLog("delete_prep_routine", "id=$id") {
        api.deletePrepRoutine(id)
    }

    suspend fun getPlannedPrepItems(date: String): Result<List<PlannedPrepItem>> = wrapWithLog("get_planned_prep_items", "date=$date") {
        api.getPlannedPrepItems(date)
    }

    suspend fun getPrepItems(date: String): Result<List<PlannedPrepItem>> = getPlannedPrepItems(date)

    suspend fun savePlannedPrepItem(request: SavePlannedPrepItemRequest): Result<AppResponse> = wrapWithLog("save_planned_prep_item", request.toString()) {
        api.savePlannedPrepItem(request)
    }

    suspend fun savePrepItem(request: SavePlannedPrepItemRequest): Result<AppResponse> = savePlannedPrepItem(request)

    suspend fun deletePlannedPrepItem(id: String): Result<AppResponse> = wrapWithLog("delete_planned_prep_item", "id=$id") {
        api.deletePlannedPrepItem(id)
    }

    suspend fun deletePrepItem(id: String): Result<AppResponse> = deletePlannedPrepItem(id)

    suspend fun togglePlannedPrepItem(id: String): Result<AppResponse> = wrapWithLog("toggle_planned_prep_item", "id=$id") {
        api.togglePlannedPrepItem(id)
    }

    suspend fun togglePrepItem(id: String): Result<AppResponse> = togglePlannedPrepItem(id)

    suspend fun getServices(): Result<List<Service>> = wrapWithLog("get_services") {
        api.getServices()
    }

    suspend fun saveService(request: SaveServiceRequest): Result<AppResponse> = wrapWithLog("save_service", request.toString()) {
        api.saveService(request)
    }

    suspend fun deleteService(id: String): Result<AppResponse> = wrapWithLog("delete_service", "id=$id") {
        api.deleteService(id)
    }

    suspend fun getTools(): Result<List<ToolRequest>> = wrapWithLog("get_tools") {
        api.getTools()
    }

    suspend fun saveTool(request: SaveToolRequest): Result<AppResponse> = wrapWithLog("save_tool", request.toString()) {
        api.saveTool(request)
    }

    suspend fun deleteTool(id: String): Result<AppResponse> = wrapWithLog("delete_tool", "id=$id") {
        api.deleteTool(id)
    }

    suspend fun getTrainingCategories(): Result<List<TrainingCategory>> = wrapWithLog("get_training_categories") {
        api.getTrainingCategories()
    }

    suspend fun saveTrainingCategory(request: SaveTrainingCategoryRequest): Result<AppResponse> = wrapWithLog("save_training_category", request.toString()) {
        api.saveTrainingCategory(request)
    }

    suspend fun deleteTrainingCategory(id: String): Result<AppResponse> = wrapWithLog("delete_training_category", "id=$id") {
        api.deleteTrainingCategory(id)
    }

    suspend fun getTrainingItems(): Result<List<TrainingItem>> = wrapWithLog("get_training_items") {
        api.getTrainingItems()
    }

    suspend fun saveTrainingItem(request: SaveTrainingItemRequest): Result<AppResponse> = wrapWithLog("save_training_item", request.toString()) {
        api.saveTrainingItem(request)
    }

    suspend fun deleteTrainingItem(id: String): Result<AppResponse> = wrapWithLog("delete_training_item", "id=$id") {
        api.deleteTrainingItem(id)
    }

    suspend fun getFileFolders(): Result<List<FileFolder>> = wrapWithLog("get_file_folders") {
        api.getFileFolders()
    }

    suspend fun saveFileFolder(request: SaveFileFolderRequest): Result<AppResponse> = wrapWithLog("save_file_folder", request.toString()) {
        api.saveFileFolder(request)
    }

    suspend fun deleteFileFolder(id: String): Result<AppResponse> = wrapWithLog("delete_file_folder", "id=$id") {
        api.deleteFileFolder(id)
    }

    suspend fun getFileItems(): Result<List<FileItem>> = wrapWithLog("get_file_items") {
        api.getFileItems()
    }

    suspend fun saveFileItem(request: SaveFileItemRequest): Result<AppResponse> = wrapWithLog("save_file_item", request.toString()) {
        api.saveFileItem(request)
    }

    suspend fun deleteFileItem(id: String): Result<AppResponse> = wrapWithLog("delete_file_item", "id=$id") {
        api.deleteFileItem(id)
    }

    suspend fun getDeliveries(date: String): Result<List<Delivery>> = wrapWithLog("get_deliveries", "date=$date") {
        api.getDeliveries(date)
    }

    suspend fun verifyReceipt(request: VerifyReceiptRequest): Result<ReceiptVerificationResult> = wrapWithLog("verify_receipt", request.toString()) {
        api.verifyReceipt(request)
    }

    suspend fun processSilentDelivery(request: ProcessSilentDeliveryRequest): Result<AppResponse> = wrapWithLog("process_silent_delivery", request.toString()) {
        api.processSilentDelivery(request)
    }

    suspend fun saveDelivery(request: SaveDeliveryRequest): Result<AppResponse> = wrapWithLog("save_delivery", request.toString()) {
        api.saveDelivery(request)
    }

    suspend fun deleteDelivery(id: String): Result<AppResponse> = wrapWithLog("delete_delivery", "id=$id") {
        api.deleteDelivery(id)
    }
}
